import { DocumentValue } from '@dittolive/ditto'
import { ActionMenu, Alert, Button, icons, stringify } from '@portal/components'
import * as Sentry from '@sentry/browser'
import { json2csvAsync } from 'json-2-csv'
import React, { useState } from 'react'

const { CloudDownload } = icons

type Props = {
  selectedCollection?: string | null
  data: DocumentValue[]
  outputPath?: string
}

type SupportedFormat = 'JSON' | 'CSV'

export default function QueryDataExport({
  selectedCollection,
  data,
  outputPath,
}: Props) {
  const [exportError, setExportError] = useState<string | null>(null)

  const handleExport = async (format: SupportedFormat) => {
    setExportError(null)

    try {
      const formattedString =
        format === 'CSV'
          ? await json2csvAsync(data, { emptyFieldValue: '' })
          : stringify(data)

      if (!formattedString) {
        throw new Error('Failed to format data')
      }

      const filename =
        outputPath?.substring(outputPath.lastIndexOf('/') + 1) ||
        `${selectedCollection || 'data'}.${format.toLowerCase()}`
      const mime = format === 'CSV' ? 'text/csv' : 'application/json'
      const blob = new Blob([formattedString], { type: mime })
      const url = window.URL.createObjectURL(blob)

      // Create temporary link and trigger download
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      Sentry.captureException(error)
      setExportError(
        'There has been a problem exporting the data. Please try again later.',
      )
    }
  }

  return (
    <>
      <ActionMenu
        groups={[
          {
            label: 'Choose an export format',
            items: [
              {
                label: 'JSON',
                onClick: () => handleExport('JSON'),
              },
              {
                label: 'CSV',
                onClick: () => handleExport('CSV'),
              },
            ],
          },
        ]}
        itemsClassName="sm:min-w-56"
        trigger={
          <Button
            variant="outline"
            aria-label="Export query result"
            disabled={!data.length}
          >
            <CloudDownload size={16} className="mr-2" />
            Export query result
          </Button>
        }
        align="end"
      />

      {exportError && <Alert variant="danger">{exportError}</Alert>}
    </>
  )
}
